import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete input backed by Nominatim via /api/geocode/suggest.
 *
 * Props:
 *   value, onChange, placeholder, required
 *   onSelect(item)  — optional callback when a suggestion is picked
 *   testId          — base data-testid; input gets `${testId}-input`, each row gets `${testId}-opt-${i}`
 */
export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = "City, country",
  required = false,
  onSelect,
  testId = "address",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");

  // Debounced suggestion fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (value || "").trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    // Don't re-fetch if user just picked an item whose label matches
    if (q === lastQueryRef.current) return;

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geocode/suggest", { params: { q, limit: 5 } });
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlighted(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (item) => {
    const label = item.short || item.display_name;
    lastQueryRef.current = label;
    onChange(label);
    onSelect?.(item);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      pick(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        type="text"
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        data-testid={`${testId}-input`}
      />
      {loading && (
        <Loader2
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground"
        />
      )}

      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg overflow-hidden max-h-72 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li key={`${s.lat}-${s.lng}-${i}`}>
              <button
                type="button"
                onClick={() => pick(s)}
                className={`w-full text-left px-3 py-2.5 text-sm flex items-start gap-2 hover:bg-secondary transition ${
                  i === highlighted ? "bg-secondary" : ""
                }`}
                data-testid={`${testId}-opt-${i}`}
              >
                <MapPin size={14} className="text-primary mt-0.5 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block font-medium truncate">{s.short || s.display_name}</span>
                  {s.short && s.short !== s.display_name && (
                    <span className="block text-xs text-muted-foreground truncate">{s.display_name}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
